import React, { useEffect } from "react";

export default function PrivacyPolicy() {
  useEffect(() => {
    document.title = "Privacy Policy | AnySkill";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', 'Privacy Policy for AnySkill talent marketplace. Learn how we collect, use, and protect your personal data.');
    }
  }, []);

  return (
    <main className="min-h-screen bg-background text-foreground">
      <section className="container mx-auto px-4 py-12 max-w-4xl">
        <h1 className="text-3xl font-bold mb-2">Privacy Policy</h1>
        <p className="text-muted-foreground mb-8">
          Last Updated: December 29, 2024
        </p>

        <article className="space-y-8 text-sm leading-relaxed">
          {/* Introduction */}
          <section>
            <h2 className="text-xl font-semibold mb-3">1. Introduction</h2>
            <p className="text-muted-foreground mb-3">
              AnySkill ("Company," "we," "us," or "our") is committed to protecting the privacy and security of your personal information. This Privacy Policy describes how we collect, use, disclose, and safeguard your information when you use our website, mobile application, and services (collectively, the "Platform").
            </p>
            <p className="text-muted-foreground mb-3">
              By accessing or using the Platform, you acknowledge that you have read, understood, and agree to be bound by this Privacy Policy. If you do not agree with the terms of this Privacy Policy, please do not access or use the Platform.
            </p>
            <p className="text-muted-foreground">
              This Privacy Policy applies to all users of the Platform, including service providers, clients, and visitors. We reserve the right to modify this Privacy Policy at any time. Any changes will be effective immediately upon posting of the revised Privacy Policy, and your continued use of the Platform after such posting constitutes your acceptance of the modified Privacy Policy.
            </p>
          </section>

          {/* Information We Collect */}
          <section>
            <h2 className="text-xl font-semibold mb-3">2. Information We Collect</h2>
            
            <h3 className="text-lg font-medium mb-2 mt-4">2.1 Personal Information You Provide</h3>
            <p className="text-muted-foreground mb-3">
              We collect personal information that you voluntarily provide when registering for an account, creating or updating your profile, posting services or requests, communicating with other users, or otherwise interacting with the Platform. This information may include, but is not limited to:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-1 mb-4">
              <li><strong>Identity Information:</strong> First name, last name, date of birth, profile photograph, and biographical information.</li>
              <li><strong>Contact Information:</strong> Email address, telephone number, and mailing address.</li>
              <li><strong>Account Credentials:</strong> Username, password, and security questions.</li>
              <li><strong>Educational Information:</strong> School name, grade level, and academic affiliations.</li>
              <li><strong>Financial Information:</strong> Payment card details, bank account information, and billing address (processed through our third-party payment processor, Stripe).</li>
              <li><strong>Professional Information:</strong> Skills, qualifications, portfolio items, work history, and service offerings.</li>
              <li><strong>Communications:</strong> Messages, feedback, reviews, and other content you submit through the Platform.</li>
            </ul>

            <h3 className="text-lg font-medium mb-2">2.2 Information Collected Automatically</h3>
            <p className="text-muted-foreground mb-3">
              When you access or use the Platform, we automatically collect certain information, including:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-1 mb-4">
              <li><strong>Device Information:</strong> Device type, operating system, unique device identifiers, browser type and version, and mobile network information.</li>
              <li><strong>Log Information:</strong> Access times, pages viewed, IP address, referring URL, and the page you visited before navigating to the Platform.</li>
              <li><strong>Location Information:</strong> General geographic location based on IP address or, with your consent, more precise location data from your mobile device.</li>
              <li><strong>Usage Information:</strong> Information about your interactions with the Platform, including features used, links clicked, search queries, and transaction history.</li>
              <li><strong>Cookies and Similar Technologies:</strong> We use cookies, web beacons, pixels, and similar tracking technologies to collect information about your browsing activities and to distinguish you from other users of the Platform.</li>
            </ul>

            <h3 className="text-lg font-medium mb-2">2.3 Information from Third Parties</h3>
            <p className="text-muted-foreground">
              We may receive information about you from third-party sources, including identity verification services, payment processors (such as Stripe), social media platforms (if you choose to link your account), and publicly available sources. We may combine this information with other information we collect about you.
            </p>
          </section>

          {/* Use of Information */}
          <section>
            <h2 className="text-xl font-semibold mb-3">3. Use of Information</h2>
            <p className="text-muted-foreground mb-3">
              We use the information we collect for various purposes, including but not limited to:
            </p>
            
            <h3 className="text-lg font-medium mb-2">3.1 Provision of Services</h3>
            <ul className="list-disc pl-6 text-muted-foreground space-y-1 mb-4">
              <li>To create, maintain, and secure your account;</li>
              <li>To facilitate transactions between service providers and clients;</li>
              <li>To process payments, refunds, and payouts through our payment processor;</li>
              <li>To enable communication between users through our messaging system;</li>
              <li>To display your profile and service listings to other users;</li>
              <li>To provide customer support and respond to your inquiries.</li>
            </ul>

            <h3 className="text-lg font-medium mb-2">3.2 Platform Improvement and Personalization</h3>
            <ul className="list-disc pl-6 text-muted-foreground space-y-1 mb-4">
              <li>To analyze usage patterns and improve the Platform's functionality;</li>
              <li>To personalize your experience and provide tailored recommendations;</li>
              <li>To develop new features, products, and services;</li>
              <li>To conduct research and analytics to better understand our users.</li>
            </ul>

            <h3 className="text-lg font-medium mb-2">3.3 Safety and Security</h3>
            <ul className="list-disc pl-6 text-muted-foreground space-y-1 mb-4">
              <li>To verify user identities and prevent fraudulent activity;</li>
              <li>To detect, investigate, and prevent security incidents and abuse;</li>
              <li>To enforce our Terms of Service and other policies;</li>
              <li>To protect the rights, property, and safety of our users and the public.</li>
            </ul>

            <h3 className="text-lg font-medium mb-2">3.4 Communications</h3>
            <ul className="list-disc pl-6 text-muted-foreground space-y-1 mb-4">
              <li>To send transactional emails regarding your account and transactions;</li>
              <li>To send service-related announcements and updates;</li>
              <li>To send promotional communications (with your consent where required by law);</li>
              <li>To notify you of changes to our policies or services.</li>
            </ul>

            <h3 className="text-lg font-medium mb-2">3.5 Legal Compliance</h3>
            <ul className="list-disc pl-6 text-muted-foreground space-y-1">
              <li>To comply with applicable laws, regulations, and legal processes;</li>
              <li>To respond to lawful requests from public authorities;</li>
              <li>To protect and defend our legal rights and interests.</li>
            </ul>
          </section>

          {/* Disclosure of Information */}
          <section>
            <h2 className="text-xl font-semibold mb-3">4. Disclosure of Information</h2>
            <p className="text-muted-foreground mb-3">
              We may share your personal information with third parties under the following circumstances:
            </p>

            <h3 className="text-lg font-medium mb-2">4.1 With Other Users</h3>
            <p className="text-muted-foreground mb-4">
              Your public profile information, including your name, profile photograph, bio, school, portfolio items, service listings, and reviews, is visible to other users of the Platform. When you engage in transactions, relevant information (such as your name and contact details) may be shared with the other party to facilitate the service.
            </p>

            <h3 className="text-lg font-medium mb-2">4.2 Service Providers and Business Partners</h3>
            <p className="text-muted-foreground mb-3">
              We share information with third-party service providers who perform services on our behalf, including:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-1 mb-4">
              <li><strong>Payment Processing:</strong> Stripe, Inc. processes all payment transactions, including credit card payments and provider payouts. Stripe's collection and use of your personal information is governed by its own privacy policy.</li>
              <li><strong>Cloud Infrastructure:</strong> We use Supabase and other cloud service providers to host and store data.</li>
              <li><strong>Analytics Providers:</strong> We use analytics services to understand Platform usage and improve our services.</li>
              <li><strong>Email and Communication Services:</strong> We use third-party services to send transactional and promotional emails.</li>
              <li><strong>Identity Verification:</strong> We may use third-party services to verify user identities for fraud prevention purposes.</li>
            </ul>

            <h3 className="text-lg font-medium mb-2">4.3 Legal Requirements and Protection</h3>
            <p className="text-muted-foreground mb-4">
              We may disclose your information if required to do so by law or in response to valid requests by public authorities (e.g., a court order, subpoena, or government agency request). We may also disclose information when we believe in good faith that disclosure is necessary to: (a) protect our rights, property, or safety; (b) protect the rights, property, or safety of our users or others; (c) investigate fraud or respond to a government request; or (d) comply with applicable law or legal process.
            </p>

            <h3 className="text-lg font-medium mb-2">4.4 Business Transfers</h3>
            <p className="text-muted-foreground mb-4">
              In the event of a merger, acquisition, reorganization, bankruptcy, or sale of all or a portion of our assets, your personal information may be transferred as part of that transaction. We will notify you via email and/or a prominent notice on the Platform of any change in ownership or uses of your personal information.
            </p>

            <h3 className="text-lg font-medium mb-2">4.5 With Your Consent</h3>
            <p className="text-muted-foreground">
              We may share your information with third parties when you have given us explicit consent to do so.
            </p>
          </section>

          {/* Method of Disclosure */}
          <section>
            <h2 className="text-xl font-semibold mb-3">5. Method of Disclosure</h2>
            <p className="text-muted-foreground mb-3">
              When we disclose personal information to third parties, we employ the following methods and safeguards:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li><strong>Encrypted Transmission:</strong> All data transmitted between your device and our servers, and between our servers and third-party service providers, is encrypted using industry-standard TLS/SSL protocols.</li>
              <li><strong>API-Based Integration:</strong> We share data with service providers through secure application programming interfaces (APIs) that limit access to only the information necessary for the specific service being provided.</li>
              <li><strong>Contractual Obligations:</strong> We require all third-party service providers to enter into data processing agreements that obligate them to protect your information and use it only for the purposes for which it was disclosed.</li>
              <li><strong>Access Controls:</strong> We implement strict access controls to ensure that only authorized personnel and systems can access personal information.</li>
              <li><strong>Data Minimization:</strong> We only share the minimum amount of information necessary to accomplish the intended purpose.</li>
            </ul>
          </section>

          {/* Data Security */}
          <section>
            <h2 className="text-xl font-semibold mb-3">6. Data Security</h2>
            <p className="text-muted-foreground mb-3">
              We implement and maintain reasonable administrative, technical, and physical security measures designed to protect your personal information from unauthorized access, use, alteration, and disclosure. These measures include, but are not limited to:
            </p>

            <h3 className="text-lg font-medium mb-2">6.1 Technical Safeguards</h3>
            <ul className="list-disc pl-6 text-muted-foreground space-y-1 mb-4">
              <li>Encryption of data in transit using TLS 1.2 or higher;</li>
              <li>Encryption of sensitive data at rest using AES-256 or equivalent encryption;</li>
              <li>Secure password hashing using bcrypt or similar algorithms;</li>
              <li>Regular security assessments and vulnerability testing;</li>
              <li>Intrusion detection and prevention systems;</li>
              <li>Firewalls and network segmentation;</li>
              <li>Regular software updates and security patches.</li>
            </ul>

            <h3 className="text-lg font-medium mb-2">6.2 Administrative Safeguards</h3>
            <ul className="list-disc pl-6 text-muted-foreground space-y-1 mb-4">
              <li>Employee training on data protection and security practices;</li>
              <li>Background checks for employees with access to personal information;</li>
              <li>Role-based access controls limiting data access to authorized personnel;</li>
              <li>Incident response procedures for handling data breaches;</li>
              <li>Regular audits of access logs and security controls.</li>
            </ul>

            <h3 className="text-lg font-medium mb-2">6.3 Physical Safeguards</h3>
            <ul className="list-disc pl-6 text-muted-foreground space-y-1 mb-4">
              <li>Data centers with 24/7 security monitoring;</li>
              <li>Access controls including biometric authentication;</li>
              <li>Environmental controls to protect against natural disasters.</li>
            </ul>

            <p className="text-muted-foreground">
              <strong>Important Notice:</strong> While we strive to protect your personal information, no method of transmission over the Internet or method of electronic storage is 100% secure. We cannot guarantee the absolute security of your information. You are responsible for maintaining the confidentiality of your account credentials and for any activities that occur under your account.
            </p>
          </section>

          {/* Data Retention */}
          <section>
            <h2 className="text-xl font-semibold mb-3">7. Data Retention</h2>
            <p className="text-muted-foreground mb-3">
              We retain your personal information for as long as necessary to fulfill the purposes for which it was collected, including to satisfy any legal, accounting, or reporting requirements. The retention period may vary depending on the context and our legal obligations.
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-1">
              <li><strong>Account Information:</strong> Retained for the duration of your account and for a reasonable period thereafter to comply with legal obligations and resolve disputes.</li>
              <li><strong>Transaction Records:</strong> Retained for a minimum of seven (7) years to comply with tax and financial reporting requirements.</li>
              <li><strong>Communications:</strong> Messages and communications may be retained for the duration of your account and for a reasonable period thereafter.</li>
              <li><strong>Log Data:</strong> Automatically collected information may be retained for up to twenty-four (24) months for security and analytical purposes.</li>
            </ul>
          </section>

          {/* Your Rights and Choices */}
          <section>
            <h2 className="text-xl font-semibold mb-3">8. Your Rights and Choices</h2>
            <p className="text-muted-foreground mb-3">
              Depending on your jurisdiction, you may have certain rights regarding your personal information:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li><strong>Access:</strong> You have the right to request access to the personal information we hold about you.</li>
              <li><strong>Correction:</strong> You have the right to request that we correct any inaccurate or incomplete personal information.</li>
              <li><strong>Deletion:</strong> You have the right to request that we delete your personal information, subject to certain exceptions.</li>
              <li><strong>Portability:</strong> You have the right to request a copy of your personal information in a structured, commonly used, machine-readable format.</li>
              <li><strong>Objection:</strong> You have the right to object to the processing of your personal information for certain purposes.</li>
              <li><strong>Restriction:</strong> You have the right to request that we restrict the processing of your personal information under certain circumstances.</li>
              <li><strong>Withdrawal of Consent:</strong> Where we rely on your consent to process personal information, you have the right to withdraw that consent at any time.</li>
            </ul>
            <p className="text-muted-foreground mt-3">
              To exercise any of these rights, please contact us at team@anyskill.app. We will respond to your request within the timeframe required by applicable law.
            </p>
          </section>

          {/* Cookies and Tracking Technologies */}
          <section>
            <h2 className="text-xl font-semibold mb-3">9. Cookies and Tracking Technologies</h2>
            <p className="text-muted-foreground mb-3">
              We use cookies and similar tracking technologies to collect and store information about your interactions with the Platform. Cookies are small text files stored on your device that help us provide and improve our services.
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-1 mb-3">
              <li><strong>Essential Cookies:</strong> Required for the Platform to function properly, including authentication and security.</li>
              <li><strong>Functional Cookies:</strong> Remember your preferences and settings to enhance your experience.</li>
              <li><strong>Analytics Cookies:</strong> Help us understand how users interact with the Platform to improve our services.</li>
            </ul>
            <p className="text-muted-foreground">
              You can manage your cookie preferences through your browser settings. Please note that disabling certain cookies may affect the functionality of the Platform.
            </p>
          </section>

          {/* Children's Privacy */}
          <section>
            <h2 className="text-xl font-semibold mb-3">10. Children's Privacy</h2>
            <p className="text-muted-foreground mb-3">
              The Platform is designed for use by individuals who are at least thirteen (13) years of age. We do not knowingly collect personal information from children under the age of thirteen (13). If you are under thirteen (13), you may not use the Platform or provide any personal information.
            </p>
            <p className="text-muted-foreground">
              For users between the ages of thirteen (13) and eighteen (18), parental or guardian consent may be required for certain activities, including receiving payouts through Stripe Connect. If we learn that we have collected personal information from a child under thirteen (13), we will take steps to delete such information promptly.
            </p>
          </section>

          {/* International Data Transfers */}
          <section>
            <h2 className="text-xl font-semibold mb-3">11. International Data Transfers</h2>
            <p className="text-muted-foreground">
              Your personal information may be transferred to, stored, and processed in countries other than your country of residence, including the United States. These countries may have data protection laws that are different from the laws of your country. By using the Platform, you consent to the transfer of your information to these countries. We take appropriate safeguards to ensure that your personal information remains protected in accordance with this Privacy Policy, including the use of standard contractual clauses approved by relevant data protection authorities.
            </p>
          </section>

          {/* Third-Party Links */}
          <section>
            <h2 className="text-xl font-semibold mb-3">12. Third-Party Links and Services</h2>
            <p className="text-muted-foreground">
              The Platform may contain links to third-party websites, applications, or services that are not operated by us. This Privacy Policy does not apply to such third-party services, and we are not responsible for their privacy practices. We encourage you to review the privacy policies of any third-party services you access through the Platform.
            </p>
          </section>

          {/* California Privacy Rights */}
          <section>
            <h2 className="text-xl font-semibold mb-3">13. California Privacy Rights</h2>
            <p className="text-muted-foreground mb-3">
              If you are a California resident, you may have additional rights under the California Consumer Privacy Act (CCPA) and the California Privacy Rights Act (CPRA), including:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-1 mb-3">
              <li>The right to know what personal information we collect, use, disclose, and sell;</li>
              <li>The right to request deletion of your personal information;</li>
              <li>The right to opt out of the sale or sharing of your personal information;</li>
              <li>The right to correct inaccurate personal information;</li>
              <li>The right to limit the use and disclosure of sensitive personal information;</li>
              <li>The right to non-discrimination for exercising your privacy rights.</li>
            </ul>
            <p className="text-muted-foreground">
              <strong>Notice:</strong> We do not sell your personal information as defined under the CCPA/CPRA. To exercise your California privacy rights, please contact us at team@anyskill.app.
            </p>
          </section>

          {/* Changes to Privacy Policy */}
          <section>
            <h2 className="text-xl font-semibold mb-3">14. Changes to This Privacy Policy</h2>
            <p className="text-muted-foreground">
              We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors. When we make material changes, we will notify you by updating the "Last Updated" date at the top of this Privacy Policy and, where required by law, we will provide additional notice (such as by email or through a notice on the Platform). We encourage you to review this Privacy Policy periodically to stay informed about how we are protecting your information.
            </p>
          </section>

          {/* Contact Information */}
          <section>
            <h2 className="text-xl font-semibold mb-3">15. Contact Us</h2>
            <p className="text-muted-foreground mb-3">
              If you have any questions, concerns, or complaints about this Privacy Policy or our data practices, or if you wish to exercise your privacy rights, please contact us at:
            </p>
            <div className="text-muted-foreground bg-muted/50 p-4 rounded-lg">
              <p><strong>AnySkill</strong></p>
              <p>Email: team@anyskill.app</p>
            </div>
            <p className="text-muted-foreground mt-3">
              We will endeavor to respond to your inquiry within a reasonable timeframe and in accordance with applicable law.
            </p>
          </section>

          {/* Acknowledgment */}
          <section className="border-t border-border pt-6 mt-8">
            <p className="text-muted-foreground italic">
              By creating an account or using the AnySkill Platform, you acknowledge that you have read and understood this Privacy Policy and agree to the collection, use, and disclosure of your personal information as described herein.
            </p>
          </section>
        </article>
      </section>
    </main>
  );
}
